import { env } from "@/core/config/env";
import { ApiError, type ProblemDetails } from "@/core/utils/errors";
import type { AutomationSettings, TenantAlertSettings, TenantPurchaseRequest } from "@/core/types/tenantWorkspace";
import type { TenantFeature, TenantFeatureDefinition } from '@/core/types/features';
import type { PlatformGuidanceGuide, SavePlatformGuidanceGuidePayload } from '@/core/types/guidance';

const platformTokenKey = "crm.platform-access-token";
const platformTokenExpiryKey = "crm.platform-access-token-expires-at";
const platformUserRoleKey = "crm.platform-user-role";
const platformUserNameKey = "crm.platform-user-name";
const platformUserPermissionsKey = "crm.platform-user-permissions";

export interface PlatformSession {
  accessToken: string;
  expiresIn: number;
  expiresAtUtc: string;
  displayName?: string | null;
  role?: string | null;
  permissions?: string[] | null;
}

export interface PlatformStaff {
  id: string;
  displayName: string;
  username: string;
  roleId: string;
  roleName: string;
  isActive: boolean;
  lastLoginAtUtc: string | null;
}

export interface PlatformRole {
  id: string;
  name: string;
  permissionCodes: string[];
  isActive: boolean;
  staffCount: number;
}

export interface PlatformPermissionDefinition {
  code: string;
  group: string;
  title: string;
}

export interface PlatformStorageSettings {
  provider: "Local" | "Arvan";
  endpoint: string;
  region: string;
  bucketName: string;
  hasAccessKey: boolean;
  hasSecretKey: boolean;
  publicBaseUrl: string;
  prefix: string;
  publicRead: boolean;
  isConfigured: boolean;
}

export interface UpdatePlatformStorageSettingsPayload {
  provider: "Local" | "Arvan";
  endpoint: string | null;
  region: string | null;
  bucketName: string | null;
  accessKey: string | null;
  secretKey: string | null;
  clearAccessKey: boolean;
  clearSecretKey: boolean;
  publicBaseUrl: string | null;
  prefix: string | null;
  publicRead: boolean;
}

export interface PlatformStorageConnectionTestResult {
  isSuccessful: boolean;
  message: string;
  elapsedMilliseconds: number;
  temporaryObjectRemoved: boolean;
}

export interface PlatformBusinessTypeAlertSettings extends TenantAlertSettings {
  businessTypeId: string;
  businessTypeCode: string;
  businessTypeName: string;
  entityName: string;
  templateName: string;
}

export interface PlatformAlertSettings extends TenantAlertSettings {
  businessTypeProfiles: PlatformBusinessTypeAlertSettings[];
}

export interface UpdatePlatformAlertSettingsPayload extends TenantAlertSettings {
  businessTypeId?: string | null;
  resetToGlobal?: boolean;
}

export type PlatformAnnouncementCategory = "information" | "warning" | "critical";

export interface PlatformAnnouncement {
  id: string;
  batchId: string;
  tenantId: string;
  tenantName: string | null;
  title: string;
  message: string;
  category: PlatformAnnouncementCategory;
  sentAtUtc: string;
  sentByDisplayName: string;
  viewedAtUtc: string | null;
  viewedByMembershipId: string | null;
  viewedByDisplayName: string | null;
  isViewed: boolean;
}

export interface SendPlatformAnnouncementPayload {
  title: string;
  message: string;
  category: PlatformAnnouncementCategory;
  sendToAllActiveTenants: boolean;
  tenantIds: string[];
}

export interface SendPlatformAnnouncementResult {
  batchId: string;
  recipientCount: number;
  sentAtUtc: string;
  sentByDisplayName: string;
  recipients: Array<{ tenantId: string; tenantName: string }>;
}

export interface PlatformStaffPerformance extends PlatformStaff {
  accountsSold: number;
  licensesRenewed: number;
  totalOperations: number;
  lastActivityAtUtc: string | null;
}

export interface PlatformActivity {
  id: string;
  actorStaffId: string | null;
  actorDisplayName: string;
  action: string;
  actionTitle: string;
  tenantId: string | null;
  tenantName: string | null;
  description: string;
  occurredAtUtc: string;
}

export interface PlatformOperationsDashboard {
  totalStaff: number;
  activeStaff: number;
  accountsSold: number;
  licensesRenewed: number;
  staff: PlatformStaffPerformance[];
  recentActivities: PlatformActivity[];
}

/** Code selected from Platform master data; it is intentionally not a fixed union. */
export type BusinessType = string;

export interface PlatformBusinessType {
  id: string;
  code: string;
  name: string;
  entityName: string;
  description: string | null;
  isActive: boolean;
  planIds: string[];
  /** Feature set that the business template applies on first company creation. */
  templateFeatureCodes: string[];
}

export interface SavePlatformBusinessTypePayload {
  name: string;
  entityName: string;
  description: string | null;
  isActive: boolean;
  planIds: string[];
  templateFeatureCodes: string[];
}

export interface PlatformTenantTemplateSuggestion {
  code: string;
  name: string;
  description: string;
}

export interface PlatformTenant {
  id: string;
  code: string;
  name: string;
  status: string;
  businessType: BusinessType;
  suggestedTemplate: PlatformTenantTemplateSuggestion | null;
  subscription: {
    planName: string;
    startsAtUtc: string;
    endsAtUtc: string;
    maxCustomerAccounts: number;
    maxStaff: number;
    maxLeads: number;
    maxCatalogItems: number;
    smsEnabled: boolean;
    smsCreditEnabled: boolean;
    smsCreditLimit: number;
    smsCreditUsed: number;
    smsCreditRemaining: number | null;
    smsCreditUnlimited: boolean;
    isActive: boolean;
    isUsable: boolean;
    planId: string | null;
  };
  usage: {
    customerAccounts: number;
    activeStaff: number;
  };
  channels: {
    emailAllowed: boolean;
    smsAllowed: boolean;
    socialAllowed: boolean;
  };
  initialOwner: {
    userId: string;
    membershipId: string;
    email: string;
    reusedExistingUser: boolean;
    mobile: string | null;
  } | null;
  administratorDisplayName: string | null;
  administratorUsername: string | null;
  administratorMobile: string | null;
  onboardingSmsSent?: boolean | null;
  onboardingSmsError?: string | null;
}

export interface PlatformPlan {
  id: string;
  name: string;
  maxCustomerAccounts: number;
  maxStaff: number;
  maxLeads: number;
  maxCatalogItems: number;
  smsEnabled: boolean;
  smsCreditEnabled: boolean;
  smsCreditLimit: number;
  emailEnabled: boolean;
  socialEnabled: boolean;
  isActive: boolean;
  businessTypeCodes: string[] | null;
  featureCodes: string[] | null;
}

export interface PlatformCredentialSmsResult {
  sent: boolean;
  message: string;
  error: string | null;
  username: string;
  mobile: string;
  loginUrl: string;
}

export interface CreatePlatformTenantPayload {
  name: string;
  code: string;
  businessType: BusinessType;
  planName: string;
  startsAtUtc: string;
  endsAtUtc: string;
  maxCustomerAccounts: number;
  maxStaff: number;
  maxLeads: number;
  maxCatalogItems: number;
  ownerDisplayName: string;
  ownerUsername: string;
  ownerEmail: string;
  ownerPassword: string;
  ownerMobile: string | null;
  planId: string | null;
  smsEnabled: boolean;
  smsCreditEnabled: boolean;
  smsCreditLimit: number;
  emailEnabled: boolean;
  socialEnabled: boolean;
  confirmExistingOwner: boolean;
}

export interface UpdatePlatformLicensePayload {
  planName: string;
  startsAtUtc: string;
  endsAtUtc: string;
  maxCustomerAccounts: number;
  maxStaff: number;
  maxLeads: number;
  maxCatalogItems: number;
  isActive: boolean;
  smsEnabled: boolean;
  emailEnabled: boolean;
  socialEnabled: boolean;
  planId: string | null;
}

const url = (path: string): string => `${env.apiBaseUrl}${path}`;

const clearPlatformSession = (): void => {
  sessionStorage.removeItem(platformTokenKey);
  sessionStorage.removeItem(platformTokenExpiryKey);
  sessionStorage.removeItem(platformUserRoleKey);
  sessionStorage.removeItem(platformUserNameKey);
  sessionStorage.removeItem(platformUserPermissionsKey);
};

const readBody = async <T>(
  response: Response,
): Promise<T | ProblemDetails | undefined> => {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get("content-type") ?? "";
  return contentType.includes("json")
    ? ((await response.json()) as T)
    : undefined;
};

export const platformRequest = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = sessionStorage.getItem(platformTokenKey);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), env.requestTimeoutMs);

  try {
    const response = await fetch(url(path), {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init.body && !(init.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
      signal: controller.signal,
    });
    const body = await readBody<T>(response);
    if (!response.ok) {
      const problem = body as ProblemDetails | undefined;
      if (response.status === 401) {
        clearPlatformSession();
        window.location.replace("/platform/login?reason=expired");
      }
      throw new ApiError(
        problem?.detail ?? problem?.title ?? "درخواست ناموفق بود.",
        response.status,
        problem,
      );
    }
    return body as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("زمان پاسخ‌گویی API به پایان رسید. از فعال‌بودن سرویس بک‌اند مطمئن شوید.", 408);
    }
    if (error instanceof TypeError) {
      throw new ApiError("ارتباط با API برقرار نشد. سرویس بک‌اند، آدرس API یا تنظیمات CORS را بررسی کنید.", 0);
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

export const platformSession = {
  role: (): string | null => sessionStorage.getItem(platformUserRoleKey),
  displayName: (): string | null => sessionStorage.getItem(platformUserNameKey),
  permissions: (): string[] => {
    try {
      const stored = sessionStorage.getItem(platformUserPermissionsKey);
      return stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      return [];
    }
  },
  hasPermission: (permission: string): boolean => {
    const permissions = platformSession.permissions();
    return permissions.includes("*") || permissions.includes(permission);
  },
  hasToken: (): boolean => {
    const token = sessionStorage.getItem(platformTokenKey);
    if (!token) return false;
    const expiry = sessionStorage.getItem(platformTokenExpiryKey);
    if (!expiry) return true;
    const expiresAt = Date.parse(expiry);
    if (Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
      clearPlatformSession();
      return false;
    }
    return true;
  },
  clear: clearPlatformSession,
  login: async (accessKey: string): Promise<PlatformSession> => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), env.requestTimeoutMs);
    try {
      const response = await fetch(url("/api/v1/platform/auth/login"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accessKey }),
        signal: controller.signal,
      });
      const body = await readBody<PlatformSession>(response);
      if (!response.ok) {
        const problem = body as ProblemDetails | undefined;
        throw new ApiError(
          problem?.detail ?? "کلید دسترسی مدیر پلتفرم معتبر نیست.",
          response.status,
          problem,
        );
      }
      const session = body as PlatformSession;
      sessionStorage.setItem(platformTokenKey, session.accessToken);
      sessionStorage.setItem(platformTokenExpiryKey, session.expiresAtUtc);
      sessionStorage.setItem(platformUserRoleKey, session.role ?? "Manager");
      sessionStorage.setItem(platformUserNameKey, session.displayName ?? "مدیر زیرساخت");
      sessionStorage.setItem(platformUserPermissionsKey, JSON.stringify(session.permissions ?? ["*"]));
      return session;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("زمان پاسخ‌گویی API به پایان رسید. سرویس بک‌اند را بررسی کنید.", 408);
      }
      if (error instanceof TypeError) {
        throw new ApiError("ارتباط با API برقرار نشد. سرویس بک‌اند، آدرس API یا تنظیمات CORS را بررسی کنید.", 0);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  },
  loginStaff: async (username: string, password: string): Promise<PlatformSession> => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), env.requestTimeoutMs);
    try {
      const response = await fetch(url("/api/v1/platform/auth/staff/login"), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      });
      const body = await readBody<PlatformSession>(response);
      if (!response.ok) {
        const problem = body as ProblemDetails | undefined;
        throw new ApiError(
          problem?.detail ?? "نام کاربری یا رمز عبور مدیر پلتفرم معتبر نیست.",
          response.status,
          problem,
        );
      }
      const session = body as PlatformSession;
      sessionStorage.setItem(platformTokenKey, session.accessToken);
      sessionStorage.setItem(platformTokenExpiryKey, session.expiresAtUtc);
      sessionStorage.setItem(platformUserRoleKey, session.role ?? "مدیر پلتفرم");
      sessionStorage.setItem(platformUserNameKey, session.displayName ?? "مدیر پلتفرم");
      sessionStorage.setItem(platformUserPermissionsKey, JSON.stringify(session.permissions ?? []));
      return session;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new ApiError("زمان پاسخ‌گویی API به پایان رسید. سرویس بک‌اند را بررسی کنید.", 408);
      }
      if (error instanceof TypeError) {
        throw new ApiError("ارتباط با API برقرار نشد. سرویس بک‌اند، آدرس API یا تنظیمات CORS را بررسی کنید.", 0);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  },
};

export const platformApi = {
  list: (): Promise<PlatformTenant[]> => platformRequest("/api/v1/platform/tenants"),
  listAnnouncements: (take = 200): Promise<PlatformAnnouncement[]> =>
    platformRequest(`/api/v1/platform/announcements?take=${take}`),
  sendAnnouncement: (payload: SendPlatformAnnouncementPayload): Promise<SendPlatformAnnouncementResult> =>
    platformRequest('/api/v1/platform/announcements', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listPlans: (): Promise<PlatformPlan[]> => platformRequest("/api/v1/platform/plans"),
  listPurchaseRequests: (status?: string): Promise<TenantPurchaseRequest[]> =>
    platformRequest(`/api/v1/platform/billing/requests${status ? `?status=${encodeURIComponent(status)}` : ""}`),
  approvePurchaseRequest: (requestId: string, note?: string | null): Promise<TenantPurchaseRequest> =>
    platformRequest(`/api/v1/platform/billing/requests/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  rejectPurchaseRequest: (requestId: string, note?: string | null): Promise<TenantPurchaseRequest> =>
    platformRequest(`/api/v1/platform/billing/requests/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  listFeatureDefinitions: (): Promise<TenantFeatureDefinition[]> =>
    platformRequest('/api/v1/platform/features/definitions'),
  tenantFeatures: (tenantId: string): Promise<TenantFeature[]> =>
    platformRequest(`/api/v1/platform/features/tenants/${tenantId}`),
  updateTenantFeatures: (tenantId: string, features: Array<{ code: string; isEnabled: boolean }>): Promise<TenantFeature[]> =>
    platformRequest(`/api/v1/platform/features/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    }),
  updatePlanFeatures: (planId: string, featureCodes: string[]): Promise<PlatformPlan> =>
    platformRequest(`/api/v1/platform/plans/${planId}/features`, {
      method: 'PUT',
      body: JSON.stringify({ featureCodes }),
    }),
  listBusinessTypes: (): Promise<PlatformBusinessType[]> =>
    platformRequest('/api/v1/platform/base-data/business-types'),
  createBusinessType: (payload: SavePlatformBusinessTypePayload & { code: string }): Promise<PlatformBusinessType> =>
    platformRequest('/api/v1/platform/base-data/business-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateBusinessType: (businessTypeId: string, payload: SavePlatformBusinessTypePayload): Promise<PlatformBusinessType> =>
    platformRequest(`/api/v1/platform/base-data/business-types/${businessTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  listGuides: (): Promise<PlatformGuidanceGuide[]> =>
    platformRequest('/api/v1/platform/guides'),
  createGuide: (payload: SavePlatformGuidanceGuidePayload): Promise<PlatformGuidanceGuide> =>
    platformRequest('/api/v1/platform/guides', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateGuide: (guideId: string, payload: SavePlatformGuidanceGuidePayload): Promise<PlatformGuidanceGuide> =>
    platformRequest(`/api/v1/platform/guides/${guideId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteGuide: (guideId: string): Promise<void> =>
    platformRequest(`/api/v1/platform/guides/${guideId}`, { method: 'DELETE' }),
  createPlan: (payload: {
    name: string;
    maxCustomerAccounts: number;
    maxStaff: number;
    maxLeads: number;
    maxCatalogItems: number;
    smsEnabled: boolean;
    smsCreditEnabled: boolean;
    smsCreditLimit: number;
    emailEnabled: boolean;
    socialEnabled: boolean;
    featureCodes: string[];
  }): Promise<PlatformPlan> =>
    platformRequest("/api/v1/platform/plans", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  create: (payload: CreatePlatformTenantPayload): Promise<PlatformTenant> =>
    platformRequest("/api/v1/platform/tenants", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateLicense: (
    tenantId: string,
    payload: UpdatePlatformLicensePayload,
  ): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/license`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  updateTenant: (
    tenantId: string,
    payload: { name: string; code: string; businessType: BusinessType },
  ): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  updateAdministrator: (
    tenantId: string,
    payload: {
      displayName: string;
      username: string;
      email: string;
      mobile: string | null;
      newPassword: string | null;
    },
  ): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/administrator`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  resendAdministratorLogin: (
    tenantId: string,
    newPassword: string,
  ): Promise<PlatformCredentialSmsResult> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/administrator/resend-login`, {
      method: "POST",
      body: JSON.stringify({ newPassword }),
    }),
  setTenantEnabled: (tenantId: string, isEnabled: boolean): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/status`, {
      method: "PUT",
      body: JSON.stringify({ isEnabled }),
    }),
  softDeleteTenant: (tenantId: string): Promise<void> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}`, {
      method: "DELETE",
    }),
  updateSmsAccess: (tenantId: string, smsEnabled: boolean): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/sms-access`, {
      method: "PUT",
      body: JSON.stringify({ smsEnabled }),
    }),
  updateChannelAccess: (
    tenantId: string,
    payload: { emailAllowed: boolean; smsAllowed: boolean; socialAllowed: boolean },
  ): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/channels`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  updateSmsCredit: (
    tenantId: string,
    payload: { smsCreditEnabled: boolean; smsCreditLimit: number },
  ): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/sms-credit`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  addSmsCredit: (tenantId: string, amount: number): Promise<PlatformTenant> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/sms-credit/top-up`, {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),
  automation: (tenantId: string): Promise<AutomationSettings> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/automation`),
  updateAutomation: (tenantId: string, payload: AutomationSettings): Promise<AutomationSettings> =>
    platformRequest(`/api/v1/platform/tenants/${tenantId}/automation`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  alertSettings: (): Promise<PlatformAlertSettings> =>
    platformRequest('/api/v1/platform/settings/alerts'),
  updateAlertSettings: (payload: UpdatePlatformAlertSettingsPayload): Promise<PlatformAlertSettings> =>
    platformRequest('/api/v1/platform/settings/alerts', {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  storageSettings: (): Promise<PlatformStorageSettings> =>
    platformRequest('/api/v1/platform/settings/storage'),
  updateStorageSettings: (payload: UpdatePlatformStorageSettingsPayload): Promise<PlatformStorageSettings> =>
    platformRequest('/api/v1/platform/settings/storage', {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  testStorageConnection: (payload: UpdatePlatformStorageSettingsPayload): Promise<PlatformStorageConnectionTestResult> =>
    platformRequest('/api/v1/platform/settings/storage/test', {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  staffDashboard: (): Promise<PlatformOperationsDashboard> =>
    platformRequest('/api/v1/platform/dashboard'),
  listStaff: (): Promise<PlatformStaff[]> =>
    platformRequest('/api/v1/platform/staff'),
  createStaff: (payload: {
    displayName: string;
    username: string;
    password: string;
    roleId: string;
  }): Promise<PlatformStaff> =>
    platformRequest('/api/v1/platform/staff', {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateStaff: (
    staffId: string,
    payload: {
      displayName: string;
      username: string;
      roleId: string;
      isActive: boolean;
      newPassword?: string | null;
    },
  ): Promise<PlatformStaff> =>
    platformRequest(`/api/v1/platform/staff/${staffId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  listRoles: (): Promise<PlatformRole[]> =>
    platformRequest('/api/v1/platform/roles'),
  listPermissionDefinitions: (): Promise<PlatformPermissionDefinition[]> =>
    platformRequest('/api/v1/platform/roles/permissions'),
  createRole: (payload: {
    name: string;
    permissionCodes: string[];
    isActive: boolean;
  }): Promise<PlatformRole> =>
    platformRequest('/api/v1/platform/roles', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateRole: (
    roleId: string,
    payload: { name: string; permissionCodes: string[]; isActive: boolean },
  ): Promise<PlatformRole> =>
    platformRequest(`/api/v1/platform/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
};
